export enum ImageFormat {
  square = 1,
  rect = 1.25,
}

export default class ImageResizeService {
  private readonly imageRequiredSquareSize = { width: 500, height: 500 };
  private readonly imageRequiredRectSize = { width: 625, height: 500 };

  /**
   * Vérifie si une image a une taille valide, sinon la redimensionne et la rogne.
   * @param file - Fichier image
   * @returns Promesse résolvant un objet contenant `valid`, `image` (Blob) ou `error`.
   */
  async validateAndResizeImage(
    file: File,
    format: ImageFormat,
  ): Promise<{ valid: boolean; resizedImage?: File; error?: string }> {
    const img = await this.loadImage(file);
    if (!img) return { valid: false, error: "image.validation.loadError" };

    const { width, height } = img;

    // Vérifie si l'image correspond déjà à une taille valide
    if (this.isValidSize(width, height)) {
      return { valid: true };
    } else if (this.isTooSmallValidSize(width, height)) {
      // Vérifie si l'image n'est pas trop petite
      return { valid: false, error: "image.validation.tooSmallImage" };
    }

    // Trouver la meilleure taille cible (ajuster selon ton besoin)
    const targetSize = ImageFormat.square === format
      ? this.imageRequiredSquareSize
      : this.imageRequiredRectSize;

    const extension = file.name.split(".").pop();
    const uuidName = `${
      Math.random().toString(36).substring(2, 10)
    }.${extension}`;

    console.log(file.name, uuidName, extension);
    // Redimensionner et rogner l'image
    const resizedImage = await this.resizeAndCrop(
      img,
      uuidName,
      targetSize.width,
      targetSize.height,
    );
    if (!resizedImage) {
      return { valid: false, error: "image.validation.conversionError" };
    }

    return { valid: true, resizedImage: resizedImage };
  }

  /**
   * Charge une image à partir d'un fichier.
   */
  private loadImage(file: File): Promise<HTMLImageElement | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Vérifie si une image a une taille valide.
   */
  private isValidSize(width: number, height: number): boolean {
    return [this.imageRequiredRectSize, this.imageRequiredSquareSize].some(
      (size) => size.width === width && size.height === height,
    );
  }

  /**
   * Vérifie si une image a une taille valide.
   */
  private isTooSmallValidSize(width: number, height: number): boolean {
    return (
      this.imageRequiredSquareSize.width > width ||
      this.imageRequiredSquareSize.height > height
    );
  }

  /**
   * Redimensionne et rogne une image pour correspondre aux dimensions cibles.
   */
  private resizeAndCrop(
    img: HTMLImageElement,
    name: string,
    targetWidth: number,
    targetHeight: number,
  ): Promise<File | null> {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        resolve(null);
        return;
      }

      // Dimensions originales
      const originalWidth = img.width;
      const originalHeight = img.height;

      // Calcul du ratio pour couvrir toute la zone sans déformation
      const scale = Math.max(
        targetWidth / originalWidth,
        targetHeight / originalHeight,
      );

      const newWidth = originalWidth * scale;
      const newHeight = originalHeight * scale;

      // Calcul du décalage pour centrer l'image rognée
      const offsetX = (newWidth - targetWidth) / 2;
      const offsetY = (newHeight - targetHeight) / 2;

      // Définition de la taille du canvas
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // Dessiner l'image redimensionnée et rognée
      ctx.drawImage(
        img,
        -offsetX, // Décalage pour centrer horizontalement
        -offsetY, // Décalage pour centrer verticalement
        newWidth,
        newHeight,
      );

      // Convertir en fichier Blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `${this.removeExtension(name)}.jpg`, {
              type: "image/jpeg",
            });
            resolve(file);
          } else {
            resolve(null);
          }
        },
        "image/jpeg",
        0.9,
      ); // Qualité JPEG à 90%
    });
  }

  removeExtension(filename: string): string {
    return filename.substring(0, filename.lastIndexOf(".")) || filename;
  }
}
